import { useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Appointment, Slot } from '@medplum/fhirtypes';
import { mergeBusyIntervals, type MinuteInterval } from '../pages/calendar/components/roomSchedulingDemoData';
import { parseDateOnlyLocal } from '@/lib/dateHelpers';

export interface UseRoomAvailabilityForDateOptions {
  date: Date | null;
  roomIds: string[];
  deviceIds: string[];
  excludeAppointmentId?: string;
  refreshKey?: string | number | boolean;
  /**
   * Extra session calendar days (YYYY-MM-DD) to include in conflict checks.
   * When set with a time window, actors busy on any session day are reported in
   * `conflictedRoomIds` / `conflictedDeviceIds`.
   */
  sessionDatesYmd?: string[];
  /** Local minutes-from-midnight for the session start (required for multi-day conflicts). */
  windowStartMinutes?: number | null;
  /** Local minutes-from-midnight for the session end. */
  windowEndMinutes?: number | null;
}

export interface UseRoomAvailabilityForDateResult {
  loading: boolean;
  error: string | null;
  /** Busy intervals on `date` (primary day) — used by RoomSelect timeline badges. */
  busyByRoom: Map<string, MinuteInterval[]>;
  busyByDevice: Map<string, MinuteInterval[]>;
  /** Rooms busy on any session day at the configured time window. */
  conflictedRoomIds: Set<string>;
  conflictedDeviceIds: Set<string>;
}

function getDayBounds(date: Date): { start: Date; end: Date } {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { start, end };
}

function clampToDay(slot: Slot, dayStart: Date, dayEnd: Date): MinuteInterval | null {
  if (!slot.start || !slot.end) return null;
  const slotStart = new Date(slot.start);
  const slotEnd = new Date(slot.end);
  if (Number.isNaN(slotStart.getTime()) || Number.isNaN(slotEnd.getTime())) return null;
  if (slotEnd <= dayStart || slotStart >= dayEnd) return null;

  const start = slotStart < dayStart ? dayStart : slotStart;
  const end = slotEnd > dayEnd ? dayEnd : slotEnd;
  return {
    startMinutes: start.getHours() * 60 + start.getMinutes(),
    endMinutes: end.getHours() * 60 + end.getMinutes(),
  };
}

function intervalsOverlapWindow(
  intervals: MinuteInterval[],
  windowStartMinutes: number,
  windowEndMinutes: number,
): boolean {
  return intervals.some(
    (interval) =>
      windowStartMinutes < interval.endMinutes && windowEndMinutes > interval.startMinutes,
  );
}

async function getExcludedSlotIds(
  medplum: ReturnType<typeof useMedplum>,
  appointmentId: string | undefined,
): Promise<Set<string>> {
  if (!appointmentId) return new Set<string>();
  const appointment: Appointment = await medplum.readResource('Appointment', appointmentId);
  return new Set(
    (appointment.slot ?? [])
      .map((slotRef) => slotRef.reference)
      .filter((reference): reference is string => Boolean(reference?.startsWith('Slot/')))
      .map((reference) => reference.slice('Slot/'.length)),
  );
}

type ActorBusyResult = {
  primaryBusy: Map<string, MinuteInterval[]>;
  conflictedIds: Set<string>;
};

async function loadBusyForActors(
  medplum: ReturnType<typeof useMedplum>,
  actorIds: string[],
  actorType: 'Location' | 'Device',
  primaryDayStart: Date,
  primaryDayEnd: Date,
  rangeStart: Date,
  rangeEnd: Date,
  sessionDays: Date[],
  windowStartMinutes: number | null,
  windowEndMinutes: number | null,
  excludedSlotIds: Set<string>,
): Promise<ActorBusyResult> {
  const primaryBusy = new Map<string, MinuteInterval[]>();
  const conflictedIds = new Set<string>();
  const hasWindow =
    windowStartMinutes != null
    && windowEndMinutes != null
    && windowEndMinutes > windowStartMinutes;

  await Promise.all(
    actorIds.map(async (actorId) => {
      const actorReference = `${actorType}/${actorId}`;
      const schedules = await medplum.searchResources('Schedule', {
        actor: actorReference,
        _count: '10',
      });
      const schedule = schedules[0];
      if (!schedule?.id) {
        primaryBusy.set(actorId, []);
        return;
      }

      const slots = await medplum.searchResources('Slot', {
        schedule: `Schedule/${schedule.id}`,
        status: 'busy',
        start: `lt${rangeEnd.toISOString()}`,
        end: `gt${rangeStart.toISOString()}`,
        _count: '500',
      });

      const relevant = slots.filter((slot) => !slot.id || !excludedSlotIds.has(slot.id));

      const primaryIntervals = mergeBusyIntervals(
        relevant
          .map((slot) => clampToDay(slot, primaryDayStart, primaryDayEnd))
          .filter((interval): interval is MinuteInterval => interval != null),
      );
      primaryBusy.set(actorId, primaryIntervals);

      if (!hasWindow) return;

      for (const day of sessionDays) {
        const { start: dayStart, end: dayEnd } = getDayBounds(day);
        const dayIntervals = relevant
          .map((slot) => clampToDay(slot, dayStart, dayEnd))
          .filter((interval): interval is MinuteInterval => interval != null);
        if (intervalsOverlapWindow(dayIntervals, windowStartMinutes, windowEndMinutes)) {
          conflictedIds.add(actorId);
          break;
        }
      }
    }),
  );

  return { primaryBusy, conflictedIds };
}

export function useRoomAvailabilityForDate(
  options: UseRoomAvailabilityForDateOptions,
): UseRoomAvailabilityForDateResult {
  const {
    date,
    roomIds,
    deviceIds,
    excludeAppointmentId,
    refreshKey,
    sessionDatesYmd,
    windowStartMinutes,
    windowEndMinutes,
  } = options;
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyByRoom, setBusyByRoom] = useState<Map<string, MinuteInterval[]>>(new Map());
  const [busyByDevice, setBusyByDevice] = useState<Map<string, MinuteInterval[]>>(new Map());
  const [conflictedRoomIds, setConflictedRoomIds] = useState<Set<string>>(new Set());
  const [conflictedDeviceIds, setConflictedDeviceIds] = useState<Set<string>>(new Set());

  const roomIdsKey = useMemo(() => [...new Set(roomIds)].sort().join('|'), [roomIds]);
  const deviceIdsKey = useMemo(() => [...new Set(deviceIds)].sort().join('|'), [deviceIds]);
  const normalizedRoomIds = useMemo(
    () => (roomIdsKey ? roomIdsKey.split('|').filter(Boolean) : []),
    [roomIdsKey],
  );
  const normalizedDeviceIds = useMemo(
    () => (deviceIdsKey ? deviceIdsKey.split('|').filter(Boolean) : []),
    [deviceIdsKey],
  );
  const dateKey = date ? date.getTime() : null;
  const sessionDatesKey = useMemo(
    () => [...new Set(sessionDatesYmd ?? [])].filter(Boolean).sort().join('|'),
    [sessionDatesYmd],
  );
  const sessionDays = useMemo(() => {
    const ymds = sessionDatesKey ? sessionDatesKey.split('|').filter(Boolean) : [];
    const parsed = ymds
      .map((ymd) => parseDateOnlyLocal(ymd))
      .filter((d): d is Date => d != null);
    if (parsed.length > 0) return parsed;
    if (dateKey == null) return [];
    return [new Date(dateKey)];
  }, [sessionDatesKey, dateKey]);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      if (dateKey == null && sessionDays.length === 0) {
        setBusyByRoom(new Map());
        setBusyByDevice(new Map());
        setConflictedRoomIds(new Set());
        setConflictedDeviceIds(new Set());
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        medplum.invalidateSearches('Schedule');
        medplum.invalidateSearches('Slot');

        const primary = dateKey != null ? new Date(dateKey) : sessionDays[0];
        const { start: primaryDayStart, end: primaryDayEnd } = getDayBounds(primary);

        const rangeStart = new Date(
          Math.min(...sessionDays.map((d) => getDayBounds(d).start.getTime()), primaryDayStart.getTime()),
        );
        const rangeEnd = new Date(
          Math.max(...sessionDays.map((d) => getDayBounds(d).end.getTime()), primaryDayEnd.getTime()),
        );

        const excludedSlotIds = await getExcludedSlotIds(medplum, excludeAppointmentId);
        const [rooms, devices] = await Promise.all([
          loadBusyForActors(
            medplum,
            normalizedRoomIds,
            'Location',
            primaryDayStart,
            primaryDayEnd,
            rangeStart,
            rangeEnd,
            sessionDays,
            windowStartMinutes ?? null,
            windowEndMinutes ?? null,
            excludedSlotIds,
          ),
          loadBusyForActors(
            medplum,
            normalizedDeviceIds,
            'Device',
            primaryDayStart,
            primaryDayEnd,
            rangeStart,
            rangeEnd,
            sessionDays,
            windowStartMinutes ?? null,
            windowEndMinutes ?? null,
            excludedSlotIds,
          ),
        ]);
        if (cancelled) return;
        setBusyByRoom(rooms.primaryBusy);
        setBusyByDevice(devices.primaryBusy);
        setConflictedRoomIds(rooms.conflictedIds);
        setConflictedDeviceIds(devices.conflictedIds);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setBusyByRoom(new Map());
        setBusyByDevice(new Map());
        setConflictedRoomIds(new Set());
        setConflictedDeviceIds(new Set());
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [
    medplum,
    dateKey,
    roomIdsKey,
    deviceIdsKey,
    normalizedRoomIds,
    normalizedDeviceIds,
    excludeAppointmentId,
    refreshKey,
    sessionDatesKey,
    sessionDays,
    windowStartMinutes,
    windowEndMinutes,
  ]);

  return {
    loading,
    error,
    busyByRoom,
    busyByDevice,
    conflictedRoomIds,
    conflictedDeviceIds,
  };
}
